import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";

/**
 * Deterministic UI-state fixture for visual regression tests.
 *
 * Not linked from the app. Used by scripts/ui-visual-snapshots/run.py to
 * capture screenshots of key UI states (default, error, focus-visible,
 * disabled, destructive text) in both light and dark mode. Each swatch
 * has a stable `data-testid` so the Playwright harness can target it.
 *
 * Query params:
 *   ?theme=dark          — toggles .dark on <html>
 *   ?focus=<data-testid> — programmatically focuses that element after
 *                          mount so :focus-visible styling is captured.
 */
export const Route = createFileRoute("/dev/a11y-states")({
  component: A11yStates,
  head: () => ({
    meta: [
      { title: "A11y States Fixture" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
});

function A11yStates() {
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("theme") === "dark") {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
    const focusId = params.get("focus");
    if (focusId && rootRef.current) {
      const el = rootRef.current.querySelector<HTMLElement>(
        `[data-testid="${CSS.escape(focusId)}"]`,
      );
      // Simulate keyboard focus so :focus-visible applies (browsers only
      // paint focus-visible for keyboard-like focus events).
      el?.focus({ preventScroll: true });
    }
  }, []);

  return (
    <div
      ref={rootRef}
      className="min-h-dvh bg-background text-foreground p-8 font-sans"
    >
      <h1 className="text-2xl font-semibold mb-6">A11y states fixture</h1>

      <section className="grid gap-8 max-w-2xl">
        <Swatch label="Button — primary" testId="swatch-btn-primary">
          <Button data-testid="btn-primary">Uložit změny</Button>
        </Swatch>

        <Swatch label="Button — destructive" testId="swatch-btn-destructive">
          <Button variant="destructive" data-testid="btn-destructive">
            Smazat účet
          </Button>
        </Swatch>

        <Swatch label="Button — outline (focus-visible)" testId="swatch-btn-outline">
          <Button variant="outline" data-testid="btn-outline">
            Zrušit
          </Button>
        </Swatch>

        <Swatch label="Button — disabled" testId="swatch-btn-disabled">
          <Button disabled data-testid="btn-disabled">
            Nedostupné
          </Button>
        </Swatch>

        <Swatch label="Input — default" testId="swatch-input-default">
          <Label htmlFor="in-def">Název</Label>
          <Input id="in-def" data-testid="input-default" defaultValue="Guláš" />
        </Swatch>

        <Swatch label="Input — error state" testId="swatch-input-error">
          <Label htmlFor="in-err">Cena</Label>
          <Input
            id="in-err"
            data-testid="input-error"
            defaultValue="-10"
            aria-invalid="true"
            aria-describedby="in-err-msg"
            className="border-destructive focus-visible:ring-destructive"
          />
          <p id="in-err-msg" role="alert" className="text-sm text-destructive mt-1">
            Cena musí být kladné číslo.
          </p>
        </Swatch>

        <Swatch label="Destructive text" testId="swatch-text-destructive">
          <p className="text-destructive">Chyba při ukládání záznamu.</p>
        </Swatch>

        <Swatch label="Badge" testId="swatch-badge">
          <div className="flex gap-2">
            <Badge>Aktivní</Badge>
            <Badge variant="secondary">Koncept</Badge>
            <Badge variant="destructive">Zamítnuto</Badge>
          </div>
        </Swatch>
      </section>
    </div>
  );
}

function Swatch({
  label,
  testId,
  children,
}: {
  label: string;
  testId: string;
  children: React.ReactNode;
}) {
  return (
    <div
      data-testid={testId}
      className="rounded-md border border-border bg-card text-card-foreground p-4"
    >
      <div className="text-xs uppercase tracking-wide text-muted-foreground mb-3">
        {label}
      </div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}
